import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, ListItem, ListItemText, ListItemButton, ListItemIcon } from '@mui/material';
import { Box } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { getAppointmentById } from '../../../api/appointmentService';
import { formatUtcToLocal } from '../../../utils/formatUtcToLocal';
import AppointmentForm from '../forms/AppointmentForm';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';
import { getPetVisitRecommendationsByPetId } from '../../../api/petService';
import CheckIcon from '@mui/icons-material/Check';

export default function RecommendedVisitsListItems({ setListEmpty = null, setDoneLoading = null, pet = null, ref, refreshUpcomingAppointments = null }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const [recommendedVisits, setRecommendedVisits] = React.useState([]);

  const [selectedAppointment, setSelectedAppointment] = React.useState(null);
  const [appointmentFormOpen, setAppointmentFormOpen] = React.useState(false);
  const [appointmentFormMode, setAppointmentFormMode] = React.useState('create');
  const [selectedRecommendedVisit, setSelectedRecommendedVisit] = React.useState(null);

  // Extract fetchRecommendedVisits so it can be reused
  const fetchRecommendedVisits = React.useCallback(async () => {
    try {
      const res = await getPetVisitRecommendationsByPetId(pet.id);
      setRecommendedVisits(res.visits);
    } catch (err) {
      
    }
  }, [pet]);

  const onAppointmentSaveSuccess = async (updatedRecord) => {
    refreshUpcomingAppointments(); // Refresh upcoming appointments
    await fetchRecommendedVisits(); // Refresh recommended visits
    setAppointmentFormOpen(false);  // Close the form
  };

  const handleAppointmentCreate = () => {
    setSelectedAppointment(null);
    setAppointmentFormMode('create');
    setAppointmentFormOpen(true);
  };

  // Expose the function to parent
  useImperativeHandle(ref, () => ({
    handleAppointmentCreate
  }));

  const handleListItemClick = (recommendedVisit) => {
    setSelectedRecommendedVisit(recommendedVisit);
    handleAppointmentCreate();
  };

  React.useEffect(() => {
    if (recommendedVisits.length !== 0) {
      if (setListEmpty) setListEmpty(false);
    }
  }, [recommendedVisits]);

  React.useEffect(() => {
    if (pet) {
      fetchRecommendedVisits();
    }
  }, [pet, fetchRecommendedVisits]);

  return (
    <>
      {recommendedVisits.length === 0 ? (
        <></>
      ) : (
      recommendedVisits.map((recommendedVisit) => (
        <ListItem 
            key={"recommendedVisit" + recommendedVisit.visit_date}
            sx={{ py: 0 }}
            alignItems="flex-start"
            secondaryAction={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <VerticalHamburgerMenu
                    options={[
                      { 
                        id: 1, 
                        name: 'Set Appointment',
                        callback: () => handleListItemClick(recommendedVisit),
                        disabled: !userHasAccess(SUB_MODULES.APPOINTMENTS, 1)
                      }
                    ]}
                  />
                </Box>

            }
        >
            <ListItemButton 
              sx={{ py: 0 }} 
              onClick={() => {if (userHasAccess(SUB_MODULES.APPOINTMENTS, 1)) handleListItemClick(recommendedVisit)}}
            >
            <ListItemIcon>
              <CheckIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" color='primary' sx={{ maxWidth: '90%', m:0 }}>
                  {`${formatUtcToLocal(recommendedVisit.visit_date, 'date')}`}
                </Typography>             
              }
              secondary={
                <Typography variant="body1" sx={{ maxWidth: '90%', m:0 }}>
                  {`${recommendedVisit.recommended_visit}`}
                </Typography>
              }
            />
            </ListItemButton>
        </ListItem>
      )))}
      <AppointmentForm
        mode={appointmentFormMode}
        appointment={selectedAppointment}
        successCallback={onAppointmentSaveSuccess}
        pet={pet} 
        recommendedVisit={selectedRecommendedVisit}
        open={appointmentFormOpen}
        onClose={() => setAppointmentFormOpen(false)}
      />
    </>
  );
}